import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apis } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export enum ITLeasingFormControls {
  itLeasingCompanyLink = 'itLeasingCompanyLink',
}

export interface ITLeasingFormValues {
  [ITLeasingFormControls.itLeasingCompanyLink]: string | null;
}

export interface ITLeasingFormState {
  createITLeasingCompanyLink: UseMutationResult<void, Error, ITLeasingFormValues, unknown>;
  form: UseFormReturn<ITLeasingFormValues>;
  handleReset: () => void;
  isLoading: boolean;
  disabled: boolean;
}

export function useITLeasingForm({ onConnected }: { onConnected?: () => void }): ITLeasingFormState {
  const { t } = useTranslation('benefitsModule');
  const { tenant, invalidateTenant, isLoading } = useTenant();
  const form = useForm<ITLeasingFormValues>({
    mode: 'onChange',
    values: { itLeasingCompanyLink: tenant?.itLeasingCompanyLink ?? '' },
  });
  const { enqueueSnackbar } = useSnackbar();
  const withMessage = useWithMessage();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const registerUrl = params.get('register-url');

    if (registerUrl) {
      form.setValue(ITLeasingFormControls.itLeasingCompanyLink, registerUrl, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form]);

  const handleReset = useCallback(() => {
    if (!tenant) {
      return;
    }
    form.reset({
      itLeasingCompanyLink: tenant.itLeasingCompanyLink,
    });
  }, [form, tenant]);

  const createITLeasingCompanyLink = useMutation({
    mutationFn: async (formValue: ITLeasingFormValues) => {
      await apis.itLeasing.updateITLeasingSettings({
        tenantId: tenant!.id,
        updateITLeasingSettingsDTO: { itLeasingCompanyLink: formValue.itLeasingCompanyLink ?? '' },
      });
    },
    onSuccess: async () => {
      enqueueSnackbar(t('IT_LEASING.connect.form.success'), { variant: 'success' });
      invalidateTenant(onConnected);
    },
    onError: withMessage,
  });

  return useMemo(() => {
    return {
      createITLeasingCompanyLink,
      form,
      handleReset,
      isLoading: isLoading || createITLeasingCompanyLink.isPending,
      disabled: !tenant || !form.formState.isDirty || !form.formState.isValid,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createITLeasingCompanyLink, form, handleReset, form.formState.isDirty, form.formState.isValid, isLoading]);
}
